import { z } from 'zod';
import { ALL_TENANT_ROLES } from './enums';

/**
 * Contrato da busca server-side da lista global de **Usuários** no admin
 * (callable `searchPlatformUsers`). Uma linha por PESSOA (`uid`), com todas as
 * empresas em que ela participa — diferente da aba "Usuários" do detalhe do
 * tenant, que lista membros de UMA empresa.
 *
 * Diferença de estratégia em relação a `tenant-search.ts`: lá os tokens viram
 * queries Firestore diretas, e por isso há combinações proibidas. Aqui o corte
 * pesado (`role`, `tenantId`) acontece no Firestore e **todo o resto é filtrado
 * em memória**, depois de agregar as memberships por uid e enriquecer com
 * `/profiles/{uid}` e Firebase Auth. Consequência deliberada: **não existe
 * combinação de filtros proibida** nesta tela.
 *
 * O preço é ler O(memberships) por busca. É adequado à escala atual e mantém
 * zero denormalização; o gatilho de migração para uma coleção derivada está
 * documentado no topo de `apps/functions/src/admin/searchPlatformUsers.ts`.
 */
export const PLATFORM_USER_SEARCH_PROPERTY_KEYS = [
  'displayName',
  'email',
  'phone',
  'role',
  'tenantId',
  'country',
  'emailVerified',
  'phoneVerified',
  'disabled',
  'joinedAt',
] as const;
export const PlatformUserSearchPropertyKeySchema = z.enum(PLATFORM_USER_SEARCH_PROPERTY_KEYS);
export type PlatformUserSearchPropertyKey = z.infer<typeof PlatformUserSearchPropertyKeySchema>;

/** Operadores expostos pelo PropertyFilter (subconjunto suportado). */
export const PLATFORM_USER_SEARCH_OPERATORS = ['=', ':', '>=', '<='] as const;
export const PlatformUserSearchOperatorSchema = z.enum(PLATFORM_USER_SEARCH_OPERATORS);
export type PlatformUserSearchOperator = z.infer<typeof PlatformUserSearchOperatorSchema>;

export const PlatformUserSearchTokenSchema = z.object({
  propertyKey: PlatformUserSearchPropertyKeySchema,
  operator: PlatformUserSearchOperatorSchema,
  /**
   * Valor sempre como string. Booleanos chegam `'true'`/`'false'` e datas como
   * epoch ms ou ISO (`YYYY-MM-DD`); a Function normaliza.
   */
  value: z.string().min(1).max(120),
});
export type PlatformUserSearchToken = z.infer<typeof PlatformUserSearchTokenSchema>;

/**
 * Papéis varridos quando o filtro não traz `role`. Default = quem a operação
 * atende no dia a dia (donos e gerentes); STAFF entra só sob demanda, porque
 * multiplica o volume por empresa e tem outro conjunto de ações úteis
 * (PIN e remoção, nunca verificação de contato).
 */
export const DEFAULT_PLATFORM_USER_ROLES = ['OWNER', 'MANAGER'] as const;

export const SearchPlatformUsersInputSchema = z.object({
  tokens: z.array(PlatformUserSearchTokenSchema).max(8).default([]),
  page: z.number().int().min(1).max(1000).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
});
export type SearchPlatformUsersInput = z.infer<typeof SearchPlatformUsersInputSchema>;

/** Uma participação da pessoa numa empresa. */
export const PlatformUserMembershipSchema = z.object({
  tenantId: z.string(),
  /** Nome da empresa, para a coluna "Empresas" não exibir só o id. */
  tenantName: z.string(),
  role: z.enum(ALL_TENANT_ROLES),
  joinedAt: z.number().int().nonnegative(),
  /**
   * Último acesso carimbado pelo cliente Android (`members.lastAccessAtMillis`).
   * A web NÃO carimba este campo — para "último acesso" da pessoa use
   * `lastSignInAt`, que vem do Firebase Auth e cobre as duas plataformas.
   */
  lastAccessAtMillis: z.number().int().nonnegative().nullable(),
});
export type PlatformUserMembership = z.infer<typeof PlatformUserMembershipSchema>;

/**
 * Uma pessoa na plataforma: identidade (Auth) + perfil (`/profiles`) +
 * participações (`members`). `emailVerified` vem do **Firebase Auth**, que é a
 * fonte de verdade do gate de contato nos dois clientes — não do espelho em
 * `/profiles` (issue #257).
 */
export const PlatformUserRowSchema = z.object({
  uid: z.string(),
  displayName: z.string(),
  email: z.string(),
  phoneE164: z.string().nullable(),
  phoneCountry: z.string().nullable(),
  emailVerified: z.boolean(),
  /** `profile.phoneVerifiedAt != null` — mesmo critério do gate da web. */
  phoneVerified: z.boolean(),
  phoneVerifiedAt: z.number().int().nonnegative().nullable(),
  /** Conta bloqueada no Firebase Auth (`disabled`). */
  disabled: z.boolean(),
  /** Millis do último login segundo o Auth; `null` se nunca entrou. */
  lastSignInAt: z.number().int().nonnegative().nullable(),
  createdAt: z.number().int().nonnegative().nullable(),
  /** `true` quando o e-mail é sintético de STAFF (`*.staff.etiquetachef.com`). */
  isStaffAccount: z.boolean(),
  /** Quem carimbou a verificação sem OTP/link, quando foi o caso. */
  preVerifiedBy: z.string().nullable(),
  memberships: z.array(PlatformUserMembershipSchema),
});
export type PlatformUserRow = z.infer<typeof PlatformUserRowSchema>;

/**
 * Resultado paginado. `truncated` avisa que a varredura bateu o teto de
 * memberships e a lista está incompleta — a UI mostra alerta pedindo filtro
 * mais específico. Truncar em silêncio leria como "cobri tudo".
 */
export type SearchPlatformUsersResult = {
  items: PlatformUserRow[];
  total: number;
  page: number;
  pageSize: number;
  truncated: boolean;
};
